import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronDown, ExternalLink, ListChecks } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

import { getSetupWizardSteps } from './setupWizardSteps'

export default function GameSetupWizard() {
  const { roomId } = useParams<{ roomId: string }>()

  const steps = useMemo(
    () => (roomId ? getSetupWizardSteps(roomId) : []),
    [roomId]
  )

  if (!roomId) {
    return (
      <div className='px-6 py-6 md:px-12 xl:px-24'>
        <div className='rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
          Room ID is required to open the Game Setup Wizard.
        </div>
      </div>
    )
  }

  return (
    <div className='px-6 py-6 md:px-12 xl:px-24 space-y-6'>
      <section className='rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 p-6 shadow-sm'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
          <div className='max-w-3xl space-y-3'>
            <div className='inline-flex items-center gap-2 rounded-full bg-[#02357212] px-3 py-1 text-sm font-medium text-[#023572]'>
              <ListChecks className='h-4 w-4' />
              <span>Game Setup Wizard</span>
            </div>
            <div className='space-y-2'>
              <h1 className='text-3xl font-bold tracking-tight text-dark-blue'>
                Introduce Step-by-Step Game Setup Wizard
              </h1>
              <p className='max-w-2xl text-base leading-7 text-slate-600'>
                Follow this guided setup flow to prepare the room in a clean,
                predictable order. Each step links directly to the part of the
                dashboard where you can preview or edit the configuration.
              </p>
            </div>
          </div>

          <div className='rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm'>
            <p className='text-sm font-medium text-slate-500'>Room ID</p>
            <p className='font-mono text-sm font-semibold text-[#023572]'>
              {roomId}
            </p>
          </div>
        </div>
      </section>

      <section
        aria-label='Game setup progress'
        className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'
      >
        <div className='flex flex-col gap-3 lg:flex-row lg:flex-wrap'>
          {steps.map((step) => (
            <div
              key={step.id}
              className='flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3'
            >
              <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#023572] text-sm font-semibold text-white'>
                {step.stepNumber}
              </div>
              <div>
                <p className='text-sm font-semibold text-dark-blue'>
                  {step.title}
                </p>
                <p className='text-xs text-slate-500'>Step {step.stepNumber}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className='grid gap-5'>
        {steps.map((step) => (
          <Card key={step.id} className='border-slate-200 shadow-sm'>
            <CardHeader className='gap-4 border-b border-slate-100 pb-5'>
              <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
                <div className='space-y-2'>
                  <div className='inline-flex w-fit items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600'>
                    Step {step.stepNumber}
                  </div>
                  <CardTitle className='text-2xl text-dark-blue'>
                    {step.title}
                  </CardTitle>
                  <CardDescription className='max-w-3xl text-sm leading-6 text-slate-600'>
                    {step.summary}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className='space-y-5 pt-6'>
              <ol className='space-y-3'>
                {step.bullets.map((bullet, index) => (
                  <li key={bullet} className='flex items-start gap-3'>
                    <div className='mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#02357212] text-sm font-semibold text-[#023572]'>
                      {index + 1}
                    </div>
                    <p className='text-sm leading-6 text-slate-700'>{bullet}</p>
                  </li>
                ))}
              </ol>

              <Collapsible className='rounded-xl border border-slate-200 bg-slate-50'>
                <CollapsibleTrigger className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-dark-blue'>
                  <span>Why this step matters</span>
                  <ChevronDown className='h-4 w-4 text-slate-500' />
                </CollapsibleTrigger>
                <CollapsibleContent className='border-t border-slate-200 px-4 py-3 text-sm leading-6 text-slate-600'>
                  {step.detail}
                </CollapsibleContent>
              </Collapsible>

              <div className='flex flex-wrap gap-3'>
                {step.links.map((link, index) => (
                  <Button
                    key={link.to}
                    asChild
                    variant={index === 0 ? 'default' : 'outline'}
                    className={cn(
                      index === 0 &&
                        'bg-dark-blue text-white hover:bg-dark-blue-hovered'
                    )}
                  >
                    <Link to={link.to} state={link.state}>
                      {link.label}
                      <ExternalLink className='h-4 w-4' />
                    </Link>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  )
}
